import { isToday } from "date-fns";
import { Suspense, useRef, useState, type PropsWithChildren, type SubmitEvent } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { EventAction, EventActionLogEntry } from "@/api/bale.zod";

import { useCreateEventComment, useGetProfileSuspense, useListEventActionLogEntriesSuspenseInfinite } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { AnchorLink } from "@/components/Anchor";
import { Button } from "@/components/Button";
import { Card, CardSubtitleHeader } from "@/components/Card";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Form } from "@/components/Form";
import { Icon } from "@/components/Icon";
import { Input, InputContainer } from "@/components/Input";
import { Missing } from "@/components/Missing";
import { TextButton } from "@/components/TextButton";
import { TextContent } from "@/components/TextContent";
import { TextLoading } from "@/components/TextLoading";
import { ViewDetector } from "@/components/ViewDetector";
import { profileAvatarUrl, profileDisplayName } from "@/model/profile";

import { useCurrentEventSuspense } from "../../hooks/useCurrentEvent";

function Fallback() {
  const { t } = useTranslation();
  return <Missing className="m-4">{t("sign-in-to-see-discussions")}</Missing>;
}

export function DiscussionsCard() {
  const { t } = useTranslation();
  return (
    <Card>
      <DragHandle>
        <CardSubtitleHeader className="border-b border-divider">{t("event-discussions")}</CardSubtitleHeader>
      </DragHandle>
      <Suspense>
        <ErrorBoundary fallback={Fallback}>
          <DiscussionsCardContent />
        </ErrorBoundary>
      </Suspense>
    </Card>
  );
}

type ActionType = EventAction["action"];
const COLLAPSIBLE = ["Updated"] as const satisfies ActionType[];
type CollapsibleActionType = (typeof COLLAPSIBLE)[number];

function isCollapsible(
  entry: EventActionLogEntry,
): entry is Extract<EventActionLogEntry, { action: CollapsibleActionType }> {
  return (COLLAPSIBLE as ActionType[]).includes(entry.action);
}

type CollapsedEntry<E extends ActionType> = E extends CollapsibleActionType
  ? { id: number; action: E; entries: Extract<EventActionLogEntry, { action: E }>[] }
  : Extract<EventActionLogEntry, { action: E }>;

function collapseRepeats(entries: EventActionLogEntry[]) {
  const collapsed: CollapsedEntry<ActionType>[] = [];
  for (const entry of entries) {
    const prev = collapsed.at(-1);
    if (isCollapsible(entry)) {
      if (prev && prev.action === entry.action) {
        prev.entries.push(entry);
      } else {
        collapsed.push({ id: entry.id, action: entry.action, entries: [entry] });
      }
    } else {
      collapsed.push(entry);
    }
  }
  return collapsed;
}

function DiscussionsCardContent() {
  const { t } = useTranslation();
  const scrollArea = useRef<HTMLDivElement>(null);
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });
  const actionLog = useListEventActionLogEntriesSuspenseInfinite(
    event.data.id,
    {},
    {
      query: {
        select: (data) => data.pages.flatMap((page) => page.data.event_action_log).toReversed(),
        getNextPageParam: (data) => data.data.page_info.next_cursor ?? undefined,
      },
    },
  );
  const createComment = useCreateEventComment();

  function handleSubmit(ev: SubmitEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    const content = (form.get("content")! as string).trim();

    createComment.mutate({ eventId: event.data.id, data: { content, reply_to_comment_id: null } });
    ev.currentTarget.reset();
  }

  const collapsed = collapseRepeats(
    actionLog.data.filter((entry) => entry.action !== "Commented" || entry.reply_to_comment_id === null),
  );

  return (
    <div className="flex flex-col">
      <div className="flex max-h-200 flex-col-reverse overflow-x-hidden overflow-y-auto" ref={scrollArea}>
        <div className="flex flex-col gap-2 p-4">
          {actionLog.hasNextPage && (
            <ViewDetector
              once
              root={scrollArea}
              onEnterView={() => actionLog.fetchNextPage()}
              className="flex flex-col items-center p-4"
            >
              <Button type="button" disabled={actionLog.isFetchingNextPage} onClick={() => actionLog.fetchNextPage()}>
                {t("load-more")}
              </Button>
            </ViewDetector>
          )}
          {collapsed.map((entry) => (
            <Suspense fallback={<TextLoading />} key={entry.id}>
              <CollapsedActionLogEntry entry={entry} entries={actionLog.data} />
            </Suspense>
          ))}
        </div>
      </div>
      <Form className="bg-white p-4 shadow-md" onSubmit={handleSubmit}>
        <InputContainer>
          <Input name="content" placeholder={t("event-new-discussion-placeholder")} />
          <ActionBox className="pr-2">
            <TextButton type="submit">
              <Icon name="send" />
            </TextButton>
          </ActionBox>
        </InputContainer>
      </Form>
    </div>
  );
}

type CommentedEntry = Extract<EventActionLogEntry, { action: "Commented" }>;

function CollapsedActionLogEntry({
  entry,
  entries,
}: {
  entry: CollapsedEntry<ActionType>;
  entries: EventActionLogEntry[];
}) {
  switch (entry.action) {
    case "Commented": {
      const replies = entries.filter(
        (sub): sub is CommentedEntry => sub.action === "Commented" && sub.reply_to_comment_id === entry.id,
      );
      return <Comment entry={entry} replies={replies} />;
    }
    case "Created":
      return <Created entry={entry} />;
    case "Joined":
      return <Joined entry={entry} />;
    case "Left":
      return <Left entry={entry} />;
    case "Updated":
      return <Updated entry={entry} />;
    default:
      entry satisfies never;
      return null;
  }
}

function Info({ children, timestamp }: PropsWithChildren<{ timestamp: Date }>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row items-baseline justify-between gap-4 font-hint text-placeholder">
      <div>{children}</div>
      <div>{isToday(timestamp) ? t("time", { date: timestamp }) : t("datetime", { date: timestamp })}</div>
    </div>
  );
}

type ActionComponentProps<Action extends ActionType> = { entry: CollapsedEntry<Action> };

function Created({ entry }: ActionComponentProps<"Created">) {
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const host = event.data.event_participants.find(
    (participant) => participant.profile.id === event.data.event.account_id,
  )!.profile;
  return (
    <Info timestamp={entry.occurred_at}>
      <Trans
        i18nKey="event-action-log-created"
        components={{ ProfileLink: <AnchorLink to={`/profile/${host.id}`} /> }}
        values={{ name: profileDisplayName(host) }}
      />
    </Info>
  );
}

function Joined({ entry }: ActionComponentProps<"Joined">) {
  const profile = useGetProfileSuspense(entry.account_id, { query: { select: (data) => data.data.profile } });
  return (
    <Info timestamp={entry.occurred_at}>
      <Trans
        i18nKey="event-action-log-joined"
        components={{ ProfileLink: <AnchorLink to={`/profile/${profile.data.id}`} /> }}
        values={{ name: profileDisplayName(profile.data) }}
      />
    </Info>
  );
}

function Left({ entry }: ActionComponentProps<"Left">) {
  const profile = useGetProfileSuspense(entry.account_id, { query: { select: (data) => data.data.profile } });
  return (
    <Info timestamp={entry.occurred_at}>
      <Trans
        i18nKey="event-action-log-left"
        components={{ ProfileLink: <AnchorLink to={`/profile/${profile.data.id}`} /> }}
        values={{ name: profileDisplayName(profile.data) }}
      />
    </Info>
  );
}

function Updated({ entry }: ActionComponentProps<"Updated">) {
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const times = entry.entries.length;
  const last = entry.entries[times - 1];

  const host = event.data.event_participants.find(
    (participant) => participant.profile.id === event.data.event.account_id,
  )!.profile;
  return (
    <Info timestamp={last.occurred_at}>
      <Trans
        i18nKey="event-action-log-updated"
        components={{ ProfileLink: <AnchorLink to={`/profile/${host.id}`} /> }}
        values={{ name: profileDisplayName(host), times }}
      />
    </Info>
  );
}

function Comment({ entry, replies }: ActionComponentProps<"Commented"> & { replies: CommentedEntry[] }) {
  const { t } = useTranslation();

  const [isReplying, setIsReplying] = useState(false);
  const createComment = useCreateEventComment();
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });

  function handleSubmit(ev: SubmitEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = new FormData(ev.currentTarget);
    const content = (form.get("content")! as string).trim();

    createComment.mutate({ eventId: event.data.id, data: { content, reply_to_comment_id: entry.id } });
    ev.currentTarget.reset();
    setIsReplying(false);
  }

  return (
    <div className="flex flex-col gap-2 border-l-2 border-divider p-2 pr-0">
      <CommentContent entry={entry} />
      <div className="ml-6 flex flex-col gap-2 empty:hidden">
        {replies.map((entry) => (
          <CommentContent entry={entry} key={entry.id} />
        ))}
      </div>
      {isReplying ? (
        <Form onSubmit={handleSubmit} className="ml-6">
          <InputContainer>
            <Input name="content" autoFocus placeholder={t("event-reply-placeholder")} />
            <ActionBox className="pr-2">
              <TextButton type="button" onClick={() => setIsReplying(false)}>
                <Icon name="trash" />
              </TextButton>
              <TextButton type="submit">
                <Icon name="send" />
              </TextButton>
            </ActionBox>
          </InputContainer>
        </Form>
      ) : (
        <div>
          <TextButton
            type="button"
            onClick={() => setIsReplying(true)}
            className="ml-6 flex flex-row items-baseline gap-1 font-hint"
          >
            <Icon name="corner-right-down" className="size-[1em]" />
            {t("reply")}
          </TextButton>
        </div>
      )}
    </div>
  );
}

function CommentContent({ entry }: { entry: CommentedEntry }) {
  const { t } = useTranslation();
  const author = useGetProfileSuspense(entry.account_id, { query: { select: (data) => data.data.profile } });

  return (
    <div className="flex flex-row gap-2">
      <div className="size-12 shrink-0 overflow-hidden rounded-full bg-water">
        <img src={profileAvatarUrl(author.data)} alt="" className="size-full object-contain" />
      </div>
      <div className="flex grow flex-col">
        <div className="flex flex-row items-baseline justify-between gap-4">
          <div className="font-interest">{profileDisplayName(author.data)}</div>
          <div className="font-hint text-placeholder">
            {isToday(entry.occurred_at)
              ? t("time", { date: entry.occurred_at })
              : t("datetime", { date: entry.occurred_at })}
          </div>
        </div>
        <TextContent content={entry.content} />
      </div>
    </div>
  );
}
